/**
 * When could these people meet?
 *
 * **Times, never names.** The server returns bare spans — no class name, no
 * room, no other dancer — because this is the one route a family reads
 * somebody else's availability through (D-419). Nothing here should ever grow
 * a field that identifies what a busy block *is*.
 */

/** How plausible a suggestion is. A ranking, never a filter. */
export type SuggestionFit = 'AllDay' | 'Evening' | 'Daytime' | 'Late'

export type Span = { start: string; end: string }

export type FreeBusyDay = {
  date: string
  /**
   * She has emptied this day — every class cancelled by her or covered.
   *
   * Shown rather than silently skipped: a day quietly missing from the
   * suggestions looks exactly like a day with no gaps, and the reader cannot
   * tell a policy from an accident.
   */
  out: boolean
  teacher: Span[]
  dancers: Span[]
  free: Span[]
  /**
   * Why she cannot be asked for any of this day, in the studio's own words, or
   * absent when this feature has nothing to say about it (D-674).
   *
   * **A day she has told the studio she is not in**, rather than one she
   * happens to be teaching straight through. On a grid the two are the same
   * blank row and mean opposite things, so the reason is the difference
   * between a parent who understands and a parent who rings up (D-209).
   *
   * **The server's sentence, never the component's** — it is prose, so it is
   * Tonya's to soften (A-16).
   */
  unavailable?: string | null
}

export type FreeBusySuggestion = {
  date: string
  start: string
  end: string
  fit: SuggestionFit
}

export type FreeBusy = {
  from: string
  to: string
  windowStart: string
  windowEnd: string
  /**
   * Where after-school begins.
   *
   * The grid narrows to this when school hours are not being shown, so it
   * draws what is on offer rather than washing the whole school day in "free"
   * — availability the same screen is saying it cannot vouch for (D-428).
   */
  eveningFrom: string
  days: FreeBusyDay[]
  suggestions: FreeBusySuggestion[]
  /**
   * What "free" means here, in Tonya's words.
   *
   * The only prose in the body, and it comes from a setting rather than this
   * file — it is the one claim on the screen a reader could act on wrongly, so
   * she can soften it without a developer (A-16).
   */
  disclosure: string
}

export async function fetchFreeBusy(
  teacherId: string,
  dancerIds: string[],
  minutes: number,
): Promise<FreeBusy> {
  const params = new URLSearchParams({ teacherId, minutes: String(minutes) })
  for (const id of dancerIds) params.append('dancerIds', id)

  const res = await fetch(`/api/scheduling/free-busy?${params}`)

  // 404 is the refusal here, not "no data" — a teacher a family has no
  // business with is deliberately indistinguishable from one who does not
  // exist, so it must not render as an empty week.
  if (res.status === 404) throw new Error('not-available')
  if (!res.ok) throw new Error(`Free/busy request failed (${res.status})`)

  const body = await res.json()

  /*
   * **Checked here so a bad shape cannot take out the screen it sits on.**
   * Rendering straight from an unvalidated body meant an unexpected 200 threw
   * inside the component and unmounted the whole booking form around it — the
   * makeup control, the room picker, everything. A thrown error at the boundary
   * lands in the caller's error branch instead, which already says "pick a time
   * below" and leaves the rest of the form alone.
   */
  if (!Array.isArray(body?.days) || !Array.isArray(body?.suggestions)) {
    throw new Error('Free/busy returned an unexpected shape')
  }

  return body
}

/**
 * Where a span sits inside the drawn window, as a percentage.
 *
 * Pure so it can be tested without a browser, and shared by every lane so the
 * teacher's row and the dancer's row cannot drift apart by a pixel.
 */
export function spanOffset(
  span: Span,
  windowStart: string,
  windowEnd: string,
): { left: number; width: number } {
  const open = minutesOf(windowStart)
  const close = minutesOf(windowEnd)
  const total = close - open

  if (total <= 0) return { left: 0, width: 0 }

  // Clamped because a class may legitimately start before the drawn window or
  // run past its end, and a negative offset paints outside its own row.
  const from = Math.max(open, Math.min(close, minutesOf(span.start)))
  const to = Math.max(open, Math.min(close, minutesOf(span.end)))

  return {
    left: ((from - open) / total) * 100,
    width: ((to - from) / total) * 100,
  }
}

/** `"16:30:00"` or `"16:30"` to minutes since midnight. */
export function minutesOf(time: string): number {
  const [h, m] = time.split(':')
  return Number(h) * 60 + Number(m ?? 0)
}

/** `"16:30:00"` to `"4:30pm"`. */
export function clockLabel(time: string): string {
  const total = minutesOf(time)
  const hour = Math.floor(total / 60)
  const minute = total % 60
  const suffix = hour >= 12 ? 'pm' : 'am'
  const twelve = hour % 12 === 0 ? 12 : hour % 12

  return `${twelve}:${String(minute).padStart(2, '0')}${suffix}`
}

/**
 * What to say beside a suggestion.
 *
 * `Daytime` is the one that matters: the app cannot see school or a day job,
 * so the slot is offered and labelled rather than removed. Saying nothing
 * would present it as though it were as good as the others.
 */
export function fitLabel(fit: SuggestionFit): string {
  if (fit === 'AllDay') return 'weekend'
  if (fit === 'Evening') return 'after class'
  // Says the hour rather than judging it: some families genuinely can (D-429).
  if (fit === 'Late') return 'late — after 9'
  return 'daytime — they may be at school'
}

/**
 * A wall-clock time, a given number of minutes later.
 *
 * <b>Shared because two screens now ask it.</b> It lived privately in
 * `SchedulingAssistant`, which was right while that was the only caller; the
 * grid needs the same answer to draw a chosen lesson at its real length
 * (D-452), and a second copy is how the two would eventually disagree about
 * where a booking ends.
 *
 * <b>Wraps at midnight rather than overflowing</b>, matching `TimeOnly` on the
 * server. Returns `HH:mm:ss`, which is the shape the API and the pickers both
 * use.
 */
export function addMinutes(start: string, minutes: number): string {
  const total = minutesOf(start) + minutes
  const h = Math.floor(total / 60) % 24
  const m = total % 60

  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`
}

/**
 * Where inside a free stretch a click actually lands (D-501).
 *
 * <b>Reported on retest, against the fix that was supposed to close it.</b>
 * D-452 made a chosen slot *draw* at the lesson's length instead of filling the
 * whole gap. It did not change what a click *chose*, which had always been the
 * start of the stretch — so on a real evening running 5:00 to 9:15 the grid
 * offered one time, five o'clock, and drew a neat 30-minute band on it:
 * <i>"It only selects the beginning 30 minutes of a time. but if i want
 * something later i can't click on a later time."</i> Making the band honest is
 * what made the single choice obvious.
 *
 * <b>Snapped down to the studio's 15-minute unit</b> (D-46), the same direction
 * `snapToSlot` rounds: a click at 5:37 means 5:30. Rounding to nearest makes the
 * boundary ambiguous — a click just below a line would jump forward, past the
 * time the pointer is on.
 *
 * <b>And clamped so the lesson still fits.</b> The stretch is what is free; a
 * start close enough to its end would hang the lesson over the thing that
 * follows, which the save then refuses. Offering a time the save refuses is the
 * failure D-450 is named for.
 *
 * <b>The clamp snaps down too, so the answer stays on the grid.</b> Clamping
 * last returned the latest start that fits — 17:20 in a fifty-minute gap — and
 * the booking form's <i>Starts</i> select is built from quarter hours only, so
 * that time matches no option and the field renders <b>blank</b> while the
 * summary beside it reads 5:20pm. Nothing is lost by snapping: offset zero
 * always fits, so there is always a step-aligned answer. Found by a cold audit
 * following the value into the form.
 */
export function startWithin(
  span: Span,
  ratio: number,
  lessonMinutes: number,
  stepMinutes = 15,
): string {
  const from = minutesOf(span.start)
  const available = minutesOf(span.end) - from

  // A gap no longer than the lesson has exactly one honest answer, and the
  // arithmetic below would otherwise clamp to a negative latest start.
  const latest = available - lessonMinutes
  if (latest <= 0) return span.start

  // NaN would otherwise reach `addMinutes` and come back as "NaN:NaN:00". The
  // only caller cannot produce one, but this is exported and tested alone.
  const along = Number.isFinite(ratio) ? ratio * available : 0

  const offset = Math.floor(Math.min(Math.max(along, 0), latest) / stepMinutes) * stepMinutes

  return addMinutes(span.start, offset)
}
